// =============================================================================
// domain/backtestBoard.ts — the arithmetic behind the Results board. #58.
// -----------------------------------------------------------------------------
// Relocated from the old client's `groupStats` / `seriesFor` / `maxDD`, which
// live inline in `app/backtest-dashboard.html:487-511` — a standalone page with
// no module system, so none of it was shared and none of it was tested.
//
// ── ⚠ WHY THIS IS NOT `backtestGroupStats` ──
//
// `domain/backtests.ts` already has stats for the Backtesting tab, and they are a
// DIFFERENT, SMALLER SET: no expectancy, no profit factor, and an E-ratio behind
// a minimum-sample gate. The board needs all three and gates the E-ratio
// differently. Two functions, deliberately, rather than one that quietly means
// something different depending on who called it.
//
// ── 🚨 THE ONE DELIBERATE DIVERGENCE FROM BETA: R WITHOUT A STOP ──
//
// Josh's call, 2026-09-03: *"Fix the R rule properly, don't replicate the bug."*
//
// Over there, a backtest recorded with no stop has no R, and the adapter
// substitutes **zero** (`app/index.html:31721`). The board then keeps that zero,
// because zero is finite (`backtest-dashboard.html:508`) — so every stopless
// sample is averaged in as "a trade that made exactly 0 R", pulling **avg R**
// toward nothing.
//
// ⚠ Beta contains the RIGHT rule too, twenty lines away: `backtestGroupStats`
// filters `r != null` (`app/index.html:30853`). The board simply uses the wrong
// one.
//
// Here a sample with no stop is EXCLUDED from the average, and `nR` reports how
// many the average actually covered — because an average over 6 of 200 samples
// is a different claim from one over 200, and the tile says which.
//
// ⚠ So the two clients will show a different "avg R" for the same data. That is
// the point. Do not "fix" it back to match in a future parity pass.
//
// ── ⚠ EXPECTANCY AND WIN RATE USE DIFFERENT DENOMINATORS, AND THAT IS BETA'S ──
//
// Win rate is over DECIDED samples (scratches excluded); expectancy divides net
// points by ALL of them, scratches included. Reproduced rather than reconciled:
// it is arguably right — a scratch is a real outcome that returned nothing, and
// expectancy is "what does one sample of this setup return on average" — and
// changing it would silently move a headline number the trader already knows.
// =============================================================================

// ⚠ THE ONE RULE for which stored confluence ids may be ranked, shared with the
// analytics card and the Coach's Report. This board used to interpret that
// column itself, which is what made it disagree with both.
import type { ResolvedMark } from './confluenceEdge';

/** One sample, in the shape the board reads. Points, never dollars. */
export interface BoardSample {
  /** 'YYYY-MM-DD'. Sorting and bucketing key. */
  date: string;
  patternId: string;
  patternName: string;
  /** Product root — MES, not MESU26. */
  instrument: string;
  session: string;
  /** Resolved marks — headings already dropped, orphans kept and flagged. */
  confluences: readonly ResolvedMark[];
  /** Net result in POINTS. Negative is a loss. */
  points: number;
  /**
   * Realised R, or NULL when no stop was recorded.
   *
   * 🚨 NULL, never 0. See the header — the zero substitution is the defect this
   * port exists to not repeat.
   */
  r: number | null;
  /** Absolute excursion, in points. */
  mae: number;
  mfe: number;
  /** 'w' | 'l' | 's' — beta's own outcome letters. */
  outcome: 'w' | 'l' | 's';
}

export interface BoardStats {
  n: number;
  wins: number;
  losses: number;
  /** Percent over DECIDED samples, or null when none are decided. */
  winRate: number | null;
  /** Net points, to one decimal — beta's rounding. */
  net: number;
  /** Points per sample, over ALL samples including scratches. See the header. */
  expectancy: number | null;
  /** Gross win ÷ gross loss. Null when nothing lost — not Infinity. */
  profitFactor: number | null;
  /** avg MFE ÷ avg MAE. Null below the MAE floor. */
  edgeRatio: number | null;
  /** Mean realised R over samples THAT HAD A STOP. */
  avgR: number | null;
  /** 🚨 How many samples `avgR` covered. Never omit this from a screen. */
  nR: number;
  avgMfe: number;
  avgMae: number;
}

/**
 * ⚠ Beta's floor, kept: below this the ratio is noise, because a near-zero
 * denominator sends it to a number that looks like a spectacular edge.
 */
export const MAE_FLOOR = 0.1;

const sum = (a: readonly number[]): number => a.reduce((x, y) => x + y, 0);

export function boardStats(samples: readonly BoardSample[]): BoardStats {
  const decided = samples.filter((t) => t.outcome !== 's');
  const wins = decided.filter((t) => t.points > 0).length;
  const losses = decided.filter((t) => t.points < 0).length;

  const net = +sum(samples.map((t) => t.points)).toFixed(1);
  const winRate = wins + losses ? (wins / (wins + losses)) * 100 : null;
  // ⚠ ALL samples, scratches included. See the header.
  const expectancy = samples.length ? net / samples.length : null;

  const grossWin = sum(samples.filter((t) => t.points > 0).map((t) => t.points));
  const grossLoss = Math.abs(sum(samples.filter((t) => t.points < 0).map((t) => t.points)));
  // ⚠ Null rather than Infinity when nothing lost — a tile renders null as an
  // em-dash, and would render Infinity as "∞", which reads as a claim.
  const profitFactor = grossLoss ? grossWin / grossLoss : null;

  const avgMfe = samples.length ? sum(samples.map((t) => t.mfe)) / samples.length : 0;
  const avgMae = samples.length ? sum(samples.map((t) => t.mae)) / samples.length : 0;
  const edgeRatio = avgMae > MAE_FLOOR ? avgMfe / avgMae : null;

  // 🚨 THE FIX. `r == null` means no stop was recorded, and that is not zero R.
  const rs = samples.map((t) => t.r).filter((r): r is number => r != null && Number.isFinite(r));
  const avgR = rs.length ? sum(rs) / rs.length : null;

  return {
    n: samples.length, wins, losses, winRate, net, expectancy,
    profitFactor, edgeRatio, avgR, nR: rs.length, avgMfe, avgMae,
  };
}

/**
 * The cumulative points curve, oldest first, starting at zero.
 *
 * ⚠ The leading 0 is beta's and is load-bearing: without it a one-sample book
 * draws a single point and no line, and the drawdown strip has no peak to
 * measure from.
 */
export function cumulativeSeries(samples: readonly BoardSample[]): number[] {
  const ordered = [...samples].sort((a, b) => a.date.localeCompare(b.date));
  let run = 0;
  const out = [0];
  for (const s of ordered) {
    run += s.points;
    out.push(+run.toFixed(2));
  }
  return out;
}

/**
 * The deepest peak-to-trough drop in a cumulative series.
 *
 * ⚠ Returns a NEGATIVE number or zero, matching beta. A caller that wants a
 * magnitude negates it; one that prints this directly gets the sign for free.
 */
export function maxDrawdown(series: readonly number[]): number {
  let peak = -Infinity;
  let worst = 0;
  for (const v of series) {
    if (v > peak) peak = v;
    const d = v - peak;
    if (d < worst) worst = d;
  }
  return worst;
}

/** The running underwater series — distance below the running peak, ≤ 0. */
export function underwaterSeries(series: readonly number[]): number[] {
  let peak = -Infinity;
  return series.map((v) => {
    if (v > peak) peak = v;
    return +(v - peak).toFixed(2);
  });
}

// ── grouping ────────────────────────────────────────────────────────────────

export interface BoardGroup<K extends string = string> {
  key: K;
  label: string;
  stats: BoardStats;
  samples: readonly BoardSample[];
}

/**
 * Group samples and compute each group's stats.
 *
 * ⚠ Groups are returned in FIRST-SEEN order, not sorted. Sorting is a view
 * decision and the pattern table owns six different ones.
 */
export function groupBy(
  samples: readonly BoardSample[],
  key: (s: BoardSample) => { key: string; label: string } | null,
): BoardGroup[] {
  const map = new Map<string, { label: string; rows: BoardSample[] }>();
  for (const s of samples) {
    const k = key(s);
    // ⚠ null means "this sample has nothing to say about this dimension" — an
    // unset session, say. Dropped rather than bucketed under a blank label,
    // which would read as a real session called "".
    if (!k) continue;
    const bucket = map.get(k.key) ?? { label: k.label, rows: [] };
    bucket.rows.push(s);
    map.set(k.key, bucket);
  }
  return [...map.entries()].map(([k, v]) => ({
    key: k, label: v.label, stats: boardStats(v.rows), samples: v.rows,
  }));
}

/**
 * The confluence STACKING ladder: does using more of them help?
 *
 * ⚠ Buckets are 0, 1, 2, 3+ — and the zero bucket is included deliberately. It
 * is the baseline the other three are being compared against, and a ladder that
 * starts at one answers a different question.
 */
export function stackingLadder(samples: readonly BoardSample[]): BoardGroup[] {
  const bucket = (n: number) => (n >= 3 ? '3+' : String(n));
  const order = ['0', '1', '2', '3+'];
  const groups = groupBy(samples, (s) => {
    const b = bucket(s.confluences.length);
    return { key: b, label: `${b} conf` };
  });
  return order
    .map((k) => groups.find((g) => g.key === k))
    .filter((g): g is BoardGroup => g !== undefined);
}

// ── time slicing, for the curve's gridlines ─────────────────────────────────

export type TimeSlice = 'weekly' | 'monthly';

export interface CurveBucket {
  /** Index into the CUMULATIVE series, which is one longer than the sample list. */
  index: number;
  label: string;
}

/**
 * Where a week or month boundary falls on the cumulative curve.
 *
 * ⚠ The index is into the SERIES, not the sample list. The series carries a
 * leading zero, so sample `i` sits at series index `i + 1` — an off-by-one here
 * puts every gridline one sample early, which on a 40-sample book is a visible
 * lie about when a drawdown started.
 *
 * ⚠ `bucketOf` is passed in rather than imported, so this stays free of any
 * particular calendar implementation and remains testable with plain strings.
 */
export function curveBuckets(
  samples: readonly BoardSample[],
  bucketOf: (iso: string) => string,
  labelOf: (bucket: string) => string,
): CurveBucket[] {
  const ordered = [...samples].sort((a, b) => a.date.localeCompare(b.date));
  const out: CurveBucket[] = [];
  let seen: string | null = null;
  ordered.forEach((s, i) => {
    if (!s.date) return;
    const b = bucketOf(s.date);
    if (b === seen) return;
    seen = b;
    out.push({ index: i + 1, label: labelOf(b) });
  });
  return out;
}
